import * as React from 'react';
import { Box, Grid } from '@mui/material';
import NumberFormat from 'react-number-format';
import {
	CerebroComplexListItem,
	CerebroDrawer,
	CerebroHeading,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	TangibleAssetTypeInput,
	CerebroStyledBox,
	CerebroTextarea,
	CerebroForm,
	DebtCapitalHeading,
} from '.';
import { ReactComponent as EquipmentFinancing } from '../icons/EquipmentFinancing.svg';
import { DealRequestAllocation, ProfileTangibleAssetType } from '../models';
import { Field, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../context';
import { useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import { getApiResponseData } from '../utils/axios';
import { DebtCapitalSectionProps } from '../../pages';
import { HtmlUtils, NumberUtils } from '../utils';
import { CerebroApiEndpoints } from '../utils/api';
import { QueryKeys } from '../hooks';
import { MaxCurrencyLength } from '../utils/platform';

export const CerebroDebtEquipment: React.FC<DebtCapitalSectionProps> = ({
	allocation,
	drawerOpenByDefault = false,
}) => {
	const api = useApi();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();
	const snackbar = useSnackbar();

	const [open, setOpen] = React.useState(drawerOpenByDefault);

	const isEmpty = isDebtEquipmentEmpty(allocation);

	const { mutate } = useMutation(
		async (values: DealRequestAllocationEquipmentValues) => {
			const payload = {
				purchaseEquipmentAmount: NumberUtils.ParseDecimal(values.purchaseEquipmentAmount) ?? undefined,
				equipmentAssetType: values.equipmentAssetType,
				purchaseVehiclesAmount: NumberUtils.ParseDecimal(values.purchaseVehiclesAmount) ?? undefined,
				vehicleAssetType: values.vehicleAssetType,
				purchaseMarineAmount: NumberUtils.ParseDecimal(values.purchaseMarineAmount) ?? undefined,
				marineAssetType: values.marineAssetType,
				purchaseHelicopterAmount: NumberUtils.ParseDecimal(values.purchaseHelicopterAmount) ?? undefined,
				helicopterAssetType: values.helicopterAssetType,
				purchaseFixedWingAircraftAmount:
					NumberUtils.ParseDecimal(values.purchaseFixedWingAircraftAmount) ?? undefined,
				fixedWingAircraftAssetType: values.fixedWingAircraftAssetType,
				equipmentDescription: values.equipmentDescription,
			};

			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), payload)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.setQueryData(QueryKeys.Allocation(guid), data);
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });

				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				setOpen(false);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
		}
	);

	const drawerRef = React.useRef<HTMLDivElement>(null);

	const amountRequested = NumberUtils.Sum(
		allocation.purchaseEquipmentAmount,
		allocation.purchaseVehiclesAmount,
		allocation.purchaseMarineAmount,
		allocation.purchaseHelicopterAmount,
		allocation.purchaseFixedWingAircraftAmount
	);

	return (
		<>
			<DebtCapitalHeading icon={EquipmentFinancing} amountRequested={amountRequested}>
				Equipment Financing
			</DebtCapitalHeading>

			{isEmpty ? (
				<CerebroStyledBox sx={{ marginBottom: 8 }}>
					<CerebroHeading size="h2" component="h2">
						No details have been added yet.
					</CerebroHeading>
					<br />
					<br />
					<CerebroPrimaryButton wide onClick={() => setOpen(true)}>
						Add Equipment Details
					</CerebroPrimaryButton>
					<br />
				</CerebroStyledBox>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }} onEdit={() => setOpen(true)}>
					<Grid container>
						{allocation.equipmentAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of machinery or equipment?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseEquipmentAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.equipmentAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.vehicleAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of rolling stock or vehicles?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseVehiclesAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.vehicleAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.marineAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of marine vessels?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseMarineAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.marineAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.helicopterAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of helicopters?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseHelicopterAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.helicopterAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.fixedWingAircraftAssetType !== 'NotApplicable' ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Purchase price of fixed wing aircraft?
								</CerebroLabel>
								<NumberFormat
									value={allocation.purchaseFixedWingAircraftAmount}
									displayType="text"
									thousandSeparator={true}
									prefix="$"
								/>{' '}
								<CerebroText variant="small" component="span">
									{allocation.fixedWingAircraftAssetType}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						<Grid item xs={12}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
								Description of equipment being financed.
							</CerebroLabel>
							<CerebroText variant="small">{allocation.equipmentDescription}</CerebroText>
						</Grid>
					</Grid>
				</CerebroComplexListItem>
			)}

			<CerebroDrawer ref={drawerRef} open={open} onClose={() => setOpen(false)} icon={EquipmentFinancing}>
				<CerebroHeading>Equipment Financing</CerebroHeading>
				<br />
				<Formik
					initialValues={{
						purchaseEquipmentAmount: NumberUtils.FormatCurrency(allocation.purchaseEquipmentAmount),
						equipmentAssetType: allocation.equipmentAssetType ?? 'NotApplicable',
						purchaseVehiclesAmount: NumberUtils.FormatCurrency(allocation.purchaseVehiclesAmount),
						vehicleAssetType: allocation.vehicleAssetType ?? 'NotApplicable',
						purchaseMarineAmount: NumberUtils.FormatCurrency(allocation.purchaseMarineAmount),
						marineAssetType: allocation.marineAssetType ?? 'NotApplicable',
						purchaseHelicopterAmount: NumberUtils.FormatCurrency(allocation.purchaseHelicopterAmount),
						helicopterAssetType: allocation.helicopterAssetType ?? 'NotApplicable',
						purchaseFixedWingAircraftAmount: NumberUtils.FormatCurrency(
							allocation.purchaseFixedWingAircraftAmount
						),
						fixedWingAircraftAssetType: allocation.fixedWingAircraftAssetType,
						equipmentDescription: allocation.equipmentDescription ?? 'NotApplicable',
					}}
					validate={validateForm}
					onSubmit={values => mutate(values)}
				>
					{({ isSubmitting, errors, touched, handleSubmit, values }) => {
						const equipmentError =
							errors.purchaseEquipmentAmount !== undefined && touched.purchaseEquipmentAmount;
						const vehicleError =
							errors.purchaseVehiclesAmount !== undefined && touched.purchaseVehiclesAmount;
						const marineError = errors.purchaseMarineAmount !== undefined && touched.purchaseMarineAmount;
						const helicopterError =
							errors.purchaseHelicopterAmount !== undefined && touched.purchaseHelicopterAmount;
						const aircraftError =
							errors.purchaseFixedWingAircraftAmount !== undefined &&
							touched.purchaseFixedWingAircraftAmount;

						return (
							<CerebroForm
								onSubmit={handleSubmit}
								flatIndicator
								scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(drawerRef.current)}
							>
								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="purchaseEquipmentAmount">
										Purchase price of machinery or equipment?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="purchaseEquipmentAmount"
										name="purchaseEquipmentAmount"
										disabled={isSubmitting}
										error={equipmentError}
										value={values.purchaseEquipmentAmount}
									/>
									<Field
										as={TangibleAssetTypeInput}
										id="equipmentAssetType"
										name="equipmentAssetType"
										disabled={isSubmitting}
										error={equipmentError}
										value={values.equipmentAssetType}
									/>
									{(touched.purchaseEquipmentAmount || touched.equipmentAssetType) &&
										errors.purchaseEquipmentAmount != null &&
										!errors.purchaseEquipmentAmount.startsWith('At least one') && (
											<CerebroText variant="error">{errors.purchaseEquipmentAmount}</CerebroText>
										)}
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="purchaseVehiclesAmount">
										Purchase price of rolling stock or vehicles?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="purchaseVehiclesAmount"
										name="purchaseVehiclesAmount"
										disabled={isSubmitting}
										error={vehicleError}
										value={values.purchaseVehiclesAmount}
									/>
									<Field
										as={TangibleAssetTypeInput}
										id="vehicleAssetType"
										name="vehicleAssetType"
										disabled={isSubmitting}
										error={vehicleError}
										value={values.vehicleAssetType}
									/>
									{(touched.purchaseVehiclesAmount || touched.vehicleAssetType) &&
										errors.purchaseVehiclesAmount != null &&
										!errors.purchaseVehiclesAmount.startsWith('At least one') && (
											<CerebroText variant="error">{errors.purchaseVehiclesAmount}</CerebroText>
										)}
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="purchaseMarineAmount">
										Purchase price of marine vessels?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="purchaseMarineAmount"
										name="purchaseMarineAmount"
										disabled={isSubmitting}
										error={marineError}
										value={values.purchaseMarineAmount}
									/>
									<Field
										as={TangibleAssetTypeInput}
										id="marineAssetType"
										name="marineAssetType"
										disabled={isSubmitting}
										error={marineError}
										value={values.marineAssetType}
									/>
									{(touched.purchaseMarineAmount || touched.marineAssetType) &&
										errors.purchaseMarineAmount != null &&
										!errors.purchaseMarineAmount.startsWith('At least one') && (
											<CerebroText variant="error">{errors.purchaseMarineAmount}</CerebroText>
										)}
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="purchaseHelicopterAmount">
										Purchase price of helicopters?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="purchaseHelicopterAmount"
										name="purchaseHelicopterAmount"
										disabled={isSubmitting}
										error={helicopterError}
										value={values.purchaseHelicopterAmount}
									/>
									<Field
										as={TangibleAssetTypeInput}
										id="helicopterAssetType"
										name="helicopterAssetType"
										disabled={isSubmitting}
										error={helicopterError}
										value={values.helicopterAssetType}
									/>
									{(touched.purchaseHelicopterAmount || touched.helicopterAssetType) &&
										errors.purchaseHelicopterAmount != null &&
										!errors.purchaseHelicopterAmount.startsWith('At least one') && (
											<CerebroText variant="error">{errors.purchaseHelicopterAmount}</CerebroText>
										)}
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="purchaseFixedWingAircraftAmount">
										Purchase price of fixed wing aircraft?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="purchaseFixedWingAircraftAmount"
										name="purchaseFixedWingAircraftAmount"
										disabled={isSubmitting}
										error={aircraftError}
										value={values.purchaseFixedWingAircraftAmount}
									/>
									<Field
										as={TangibleAssetTypeInput}
										id="fixedWingAircraftAssetType"
										name="fixedWingAircraftAssetType"
										disabled={isSubmitting}
										error={aircraftError}
										value={values.fixedWingAircraftAssetType}
									/>
									{(touched.purchaseFixedWingAircraftAmount || touched.fixedWingAircraftAssetType) &&
										errors.purchaseFixedWingAircraftAmount != null &&
										!errors.purchaseFixedWingAircraftAmount.startsWith('At least one') && (
											<CerebroText variant="error">
												{errors.purchaseFixedWingAircraftAmount}
											</CerebroText>
										)}
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel
										htmlFor="equipmentDescription"
										error={touched.equipmentDescription && errors.equipmentDescription != null}
									>
										Description
									</CerebroLabel>
									<Field
										as={CerebroTextarea}
										id="equipmentDescription"
										name="equipmentDescription"
										rows={4}
										disabled={isSubmitting}
										value={values.equipmentDescription}
										error={touched.equipmentDescription && errors.equipmentDescription != null}
									/>
									{touched.equipmentDescription && errors.equipmentDescription != null && (
										<CerebroText variant="error">{errors.equipmentDescription}</CerebroText>
									)}
								</Box>

								{errors.purchaseEquipmentAmount &&
									errors.purchaseEquipmentAmount.startsWith('At least one') && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.purchaseEquipmentAmount}
										</CerebroText>
									)}

								<Grid container justifyContent="space-between" sx={{ marginTop: 5 }}>
									<Grid item>
										<CerebroPrimaryButton loading={isSubmitting} type="submit">
											Save Details
										</CerebroPrimaryButton>
									</Grid>
								</Grid>
							</CerebroForm>
						);
					}}
				</Formik>
			</CerebroDrawer>
		</>
	);
};

function validateForm(values: DealRequestAllocationEquipmentValues) {
	const errors: DealRequestAllocationEquipmentValueErrors = {};

	if (values.equipmentAssetType !== 'NotApplicable' && !values.purchaseEquipmentAmount) {
		errors.purchaseEquipmentAmount = 'Must provide a machinery or equipment amount.';
	} else if (values.equipmentAssetType === 'NotApplicable' && values.purchaseEquipmentAmount) {
		errors.purchaseEquipmentAmount = 'Please select whether the machinery or equipment is New or Used.';
	}

	if (values.vehicleAssetType !== 'NotApplicable' && !values.purchaseVehiclesAmount) {
		errors.purchaseVehiclesAmount = 'Must provide a rolling stock or vehicles amount.';
	} else if (values.vehicleAssetType === 'NotApplicable' && values.purchaseVehiclesAmount) {
		errors.purchaseVehiclesAmount = 'Please select whether the rolling stock or vehicles is New or Used.';
	}

	if (values.marineAssetType !== 'NotApplicable' && !values.purchaseMarineAmount) {
		errors.purchaseMarineAmount = 'Must provide a marine vessel amount.';
	} else if (values.marineAssetType === 'NotApplicable' && values.purchaseMarineAmount) {
		errors.purchaseMarineAmount = 'Please select whether the marine vessel is New or Used.';
	}

	if (values.helicopterAssetType !== 'NotApplicable' && !values.purchaseHelicopterAmount) {
		errors.purchaseHelicopterAmount = 'Must provide a helicopter amount.';
	} else if (values.helicopterAssetType === 'NotApplicable' && values.purchaseHelicopterAmount) {
		errors.purchaseHelicopterAmount = 'Please select whether the helicopter is New or Used.';
	}

	if (values.fixedWingAircraftAssetType !== 'NotApplicable' && !values.purchaseFixedWingAircraftAmount) {
		errors.purchaseFixedWingAircraftAmount = 'Must provide a fixed wing aircraft amount.';
	} else if (values.fixedWingAircraftAssetType === 'NotApplicable' && values.purchaseFixedWingAircraftAmount) {
		errors.purchaseFixedWingAircraftAmount = 'Please select whether the fixed wing aircraft is New or Used.';
	}

	if (
		values.equipmentAssetType === 'NotApplicable' &&
		!values.purchaseEquipmentAmount &&
		values.vehicleAssetType === 'NotApplicable' &&
		!values.purchaseVehiclesAmount &&
		values.marineAssetType === 'NotApplicable' &&
		!values.purchaseMarineAmount &&
		values.helicopterAssetType === 'NotApplicable' &&
		!values.purchaseHelicopterAmount &&
		values.fixedWingAircraftAssetType === 'NotApplicable' &&
		!values.purchaseFixedWingAircraftAmount
	) {
		errors.purchaseEquipmentAmount = 'At least one equipment type must be selected';
		errors.purchaseVehiclesAmount = 'At least one equipment type must be selected';
		errors.purchaseMarineAmount = 'At least one equipment type must be selected';
		errors.purchaseHelicopterAmount = 'At least one equipment type must be selected';
		errors.purchaseFixedWingAircraftAmount = 'At least one equipment type must be selected';
	}

	if (!values.equipmentDescription) {
		errors.equipmentDescription = 'Description is required!';
	}

	return errors;
}

interface DealRequestAllocationEquipmentValues {
	purchaseEquipmentAmount?: string;
	equipmentAssetType?: ProfileTangibleAssetType;
	purchaseVehiclesAmount?: string;
	vehicleAssetType?: ProfileTangibleAssetType;
	purchaseMarineAmount?: string;
	marineAssetType?: ProfileTangibleAssetType;
	purchaseHelicopterAmount?: string;
	helicopterAssetType?: ProfileTangibleAssetType;
	purchaseFixedWingAircraftAmount?: string;
	fixedWingAircraftAssetType?: ProfileTangibleAssetType;
	equipmentDescription?: string;
}

interface DealRequestAllocationEquipmentValueErrors {
	purchaseEquipmentAmount?: string;
	purchaseVehiclesAmount?: string;
	purchaseMarineAmount?: string;
	purchaseHelicopterAmount?: string;
	purchaseFixedWingAircraftAmount?: string;
	equipmentDescription?: string;
}

export function isDebtEquipmentEmpty(allocation: DealRequestAllocation): boolean {
	return (
		allocation.equipmentAssetType === 'NotApplicable' &&
		allocation.marineAssetType === 'NotApplicable' &&
		allocation.fixedWingAircraftAssetType === 'NotApplicable' &&
		allocation.helicopterAssetType === 'NotApplicable' &&
		allocation.vehicleAssetType === 'NotApplicable'
	);
}
